<template>
  <div
    id="freebets-campaign-generator-view"
    :class="`min-h-screen px-4 md:!px-1.5 py-4 transition-all duration-300 ${
      isMenuOpen ? 'md:ml-64' : 'md:ml-20'
    }`"
  >
    <div v-if="isLoading" class="flex items-center justify-center min-h-[60vh]">
      <Loader />
    </div>

    <div v-else class="max-w-full mx-auto">
      <header class="mb-8">
        <div class="flex flex-col gap-1">
          <h1 class="text-3xl font-extrabold text-slate-800 tracking-tight">
            {{ $route.params.id ? "Update" : "Create" }} Challenge
            <span class="text-emerald-600" v-if="$route.params.id"
              >#{{ $route.params.id }}</span
            >
          </h1>
          <p class="text-slate-500 font-medium">
            Configure your challenge campaign settings
          </p>
        </div>
      </header>

      <div class="space-y-6">
        <section
          class="bg-white rounded-3xl p-6 shadow-sm border border-slate-100"
        >
          <div class="flex items-center gap-3 mb-6">
            <i
              data-v-233e46ef=""
              class="fa-solid fa-gears text-gray-400 w-10 h-10 bg-gray-300/50 rounded-xl flex items-center justify-center"
            ></i>
            <h2 class="font-semibold text-gray-800">
              General Settings ({{ formData.skin_id }})
            </h2>
          </div>

          <div class="grid grid-cols-1 md:grid-cols-2 gap-x-3 gap-y-1">
            <div class="md:col-span-2 space-y-1.5">
              <label
                class="block text-sm font-semibold text-slate-700 uppercase tracking-wider"
                >Title*</label
              >
              <p class="text-xs text-slate-400">Internal campaign title</p>
              <input
                v-model="formData.title"
                type="text"
                class="w-full custom-input px-4 py-2.5 rounded-xl border border-slate-200 focus:ring-4 focus:ring-emerald-50 outline-none transition-all focus:border-emerald-500"
                :class="{ 'border-red-500 bg-red-50': validationErrors.title }"
                placeholder="Enter campaign title"
              />
            </div>

            <div class="space-y-1.5">
              <label
                class="block text-sm font-semibold text-slate-700 uppercase tracking-wider"
                >Mode*</label
              >
              <p class="text-xs text-slate-400">Mode of campaign</p>
              <select
                v-model="formData.mode"
                class="w-full custom-input px-4 py-2.5 rounded-xl border border-slate-200 focus:ring-4 focus:ring-emerald-50 outline-none transition-all focus:border-emerald-500 bg-white"
                :class="{ 'border-red-500 bg-red-50': validationErrors.mode }"
              >
                <option
                  v-for="(label, val) in modeOptions"
                  :key="val"
                  :value="val"
                >
                  {{ label }}
                </option>
              </select>
            </div>

            <div
              v-if="Object.keys(gameChoose).length > 0"
              class="space-y-1 relative"
              v-click-outside="() => closeDropdown('game')"
            >
              <label class="block text-sm font-semibold text-slate-700 uppercase tracking-wider"> 
                  Choose Games*</label>
                  <p class="text-xs text-slate-400">
                   Select the games for which you want to activate free bets. If you don't select any games, you can activate them across the entire provider
                 </p>

              <div @click="toggleDropdown('game')"
                    class="custom-input flex items-center justify-between cursor-pointer bg-white min-h-[42px] flex-wrap gap-2 p-2"
                    :class="{
                      '!border !border-slate-200 focus:!ring-4 focus:!ring-emerald-500/10 focus:!border-emerald-500 active:!border-emerald-500  active:!ring-emerald-500/10': activeDropdown === 'game',
                      'border-red-500': validationErrors.game_id,
                    }">
                    <div v-if="formData.games && formData.games.length > 0" class="flex flex-wrap gap-2">
                      <span v-for="id in formData.games" :key="id"
                        class="flex items-center gap-1 bg-emerald-100 text-emerald-700 px-2 py-1 rounded-md text-sm font-medium border border-emerald-200">
                        {{ gameChoose[id] }}
                        <button type="button" @click.stop="removeGame(id)"
                          class="hover:text-emerald-900 ml-1 transition-colors">
                          <i class="fa-solid fa-xmark text-xs"></i>
                        </button>
                      </span>
                    </div>

                    <span v-else class="text-slate-400 italic">Select games...</span>

                    <i class="fa-solid fa-chevron-down text-slate-300 transition-transform ml-auto"
                      :class="{ 'rotate-180': activeDropdown === 'game' }"></i>
                  </div>

              <div
                v-if="activeDropdown === 'game'"
                class="absolute dark:bg-slate-800 z-[100] w-full mt-1 bg-white border border-slate-200 rounded-xl shadow-xl overflow-hidden animate-in fade-in zoom-in duration-150"
              >
                <div class="p-2 border-b border-slate-100 bg-slate-50">
                  <input
                    v-model="searchQueries.game"
                    type="text"
                    class="w-full px-2 py-1.5 text-xs border border-slate-200 rounded-md outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-100 custom-input"
                    placeholder="Search game..."
                  />
                </div>

                <ul class="max-h-48 overflow-y-auto custom-scrollbar">
                  <li
                    v-for="(label, value) in filteredGames"
                    :key="value"
                    @click="selectGame(value)"
                    class="px-3 py-2.5 hover:bg-blue-50 cursor-pointer flex items-center justify-between text-xs transition-colors group"
                    :class="{ 'bg-blue-50/50': formData.game_id === value }"
                  >
                    <div class="flex flex-col">
                      <span
                        class="font-bold uppercase transition-colors"
                        :class="
                          formData.game_id === value
                            ? 'text-blue-600'
                            : 'text-slate-700'
                        "
                      >
                        {{ label }}
                      </span>
                      <span class="text-slate-400 text-[10px]">{{
                        value
                      }}</span>
                    </div>

                    <div class="flex items-center">
                      <i
                        v-if="formData.game_id === value"
                        class="fa-solid fa-check text-blue-500 animate-in zoom-in duration-200"
                      ></i>
                    </div>
                  </li>

                  <li
                    v-if="Object.keys(filteredGames).length === 0"
                    class="px-3 py-4 text-center text-slate-400 text-xs"
                  >
                    No games found
                  </li>
                </ul>
              </div>
            </div>

            <div class="md:col-span-2 space-y-1.5">
              <label
                class="block text-sm font-semibold text-slate-700 uppercase tracking-wider"
                >Description*</label
              >
              <p class="text-xs text-slate-400">Internal description</p>
              <textarea
                v-model="formData.description"
                rows="3"
                class="w-full px-4 py-2.5 rounded-xl border border-slate-200 focus:ring-4 focus:ring-emerald-50 outline-none transition-all focus:border-emerald-500 resize-none custom-input"
                :class="{
                  'border-red-500 bg-red-50': validationErrors.description,
                }"
                placeholder="Enter description"
              ></textarea>
            </div>

            <div class="md:col-span-2 space-y-1.5">
              <label
                class="block text-sm font-semibold text-slate-700 uppercase tracking-wider"
                >Note</label
              >
              <p class="text-xs text-slate-400">Internal note</p>
              <textarea
                v-model="formData.note"
                rows="2"
                class="w-full px-4 py-2.5 rounded-xl border border-slate-200 focus:ring-4 focus:ring-emerald-50 outline-none transition-all focus:border-emerald-500 resize-none custom-input"
                :class="{ 'border-red-500 bg-red-50': validationErrors.note }"
                placeholder="Internal notes"
              ></textarea>
            </div>
          </div>
        </section>

        <section
          class="bg-white rounded-3xl p-6 shadow-sm border border-slate-100"
        >
          <div class="mb-3 pb-0 flex items-center gap-3">
            <i
              class="fa-solid fa-timeline text-blue-400 w-10 h-10 bg-blue-300/50 rounded-xl flex items-center justify-center"
            ></i>
            <h2 class="font-semibold text-gray-800">
              Date &amp; Time Settings
            </h2>
          </div>

          <div class="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div class="space-y-1.5">
              <label
                class="block text-sm font-semibold text-slate-700 uppercase tracking-wider"
                >Announcement Date*</label
              >
              <p class="text-[10px] leading-tight text-slate-400">
                Visible but not active
              </p>
              <flat-pickr
                v-model="formData.announcement_date"
                class="w-full px-4 py-2.5 rounded-xl border border-slate-200 focus:ring-4 focus:ring-blue-50 outline-none transition-all bg-white"
                :config="{
                  enableTime: true,
                  time_24hr: true,
                  dateFormat: 'd/m/Y H:i',
                  minDate: minDateNow(),
                  maxDate: formData.end_date || null,
                }"
              />
            </div>

            <div class="space-y-1.5">
              <label
                class="block text-sm font-semibold text-slate-700 uppercase tracking-wider"
                >{{ $t("rain.start_date") || "Start Date" }}*</label
              >
              <p class="text-[10px] leading-tight text-slate-400">
                Campaign start
              </p>
              <flat-pickr
                v-model="formData.start_date"
                class="w-full px-4 py-2.5 rounded-xl border border-slate-200 focus:ring-4 focus:ring-blue-50 outline-none transition-all bg-white"
                :config="{
                  enableTime: true,
                  time_24hr: true,
                  dateFormat: 'd/m/Y H:i',
                  minDate: minDateNow(),
                  maxDate: formData.end_date || null,
                }"
              />
            </div>

            <div class="space-y-1.5">
              <label
                class="block text-sm font-semibold text-slate-700 uppercase tracking-wider"
                >{{ $t("rain.end_date") || "End Date" }}*</label
              >
              <p class="text-[10px] leading-tight text-slate-400">
                Campaign end
              </p>
              <flat-pickr
                v-model="formData.end_date"
                class="w-full px-4 py-2.5 rounded-xl border border-slate-200 focus:ring-4 focus:ring-blue-50 outline-none transition-all bg-white"
                :config="{
                  enableTime: true,
                  time_24hr: true,
                  dateFormat: 'd/m/Y H:i',
                  minDate: formData.start_date,
                }"
              />
            </div>
          </div>
        </section>

        <section
          class="bg-white rounded-3xl p-6 shadow-sm border border-slate-100"
        >
          <div class="pb-0 flex items-center gap-3 mb-3">
            <i
              class="fa-solid fa-bolt text-yellow-500 w-10 h-10 bg-slate-100 rounded-xl flex items-center justify-center"
            ></i>
            <h2 class="font-semibold text-gray-800">Configuration</h2>
          </div>

          <div class="grid grid-cols-1 md:grid-cols-4 gap-6">
            <div class="space-y-1.5">
              <label
                class="block text-sm font-semibold text-slate-700 uppercase tracking-wider"
                >Currency*</label
              >
              <select
                v-model="formData.currency"
                class="w-full px-4 py-2.5 rounded-xl border border-slate-200 outline-none transition-all focus:border-amber-500"
              >
                <option
                  v-for="opt in currenciesOpt"
                  :key="opt.code"
                  :value="opt.code"
                >
                  {{ opt.code }} - {{ opt.name }}
                </option>
              </select>
            </div>
            <div class="space-y-1.5">
              <label
                class="block text-sm font-semibold text-slate-700 uppercase tracking-wider"
                >Min Bet*</label
              >
              <input
                v-model="formData.min_bet"
                type="text"
                class="w-full px-4 py-2.5 rounded-xl border border-slate-200 outline-none focus:border-amber-500 custom-input"
                @input="handleNumericInput($event, 'min_bet')"
              />
            </div>
            <div class="space-y-1.5">
              <label
                class="block text-sm font-semibold text-slate-700 uppercase tracking-wider"
                >Max Bet*</label
              >
              <input
                v-model="formData.max_bet"
                type="text"
                class="w-full px-4 py-2.5 rounded-xl border border-slate-200 outline-none focus:border-amber-500 custom-input"
                @input="handleNumericInput($event, 'max_bet')"
              />
            </div>
            <div class="space-y-1.5 flex flex-col justify-center">
              <label
                class="block text-sm font-semibold text-slate-700 uppercase tracking-wider"
                >Status*</label
              >
              <div class="flex items-center gap-3 pt-2">
                <label class="relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    v-model="formData.is_enabled"
                    class="sr-only peer"
                  />
                  <div
                    class="w-11 h-6 bg-slate-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-emerald-100 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-emerald-500"
                  ></div>
                </label>
                <span
                  class="text-sm font-medium"
                  :class="
                    formData.is_enabled ? 'text-emerald-600' : 'text-slate-400'
                  "
                >
                  {{
                    formData.is_enabled
                      ? $t("detail.is_active") || "Active"
                      : $t("detail.is_inactive") || "Inactive"
                  }}
                </span>
              </div>
            </div>
          </div>
        </section>

        <section
          class="bg-white rounded-3xl p-6 shadow-sm border border-slate-100"
        >
          <div class="pb-0 flex items-center gap-3 mb-3">
            <i
              class="fa-solid fa-face-smile text-blue-500 w-10 h-10 bg-blue-100 rounded-xl flex items-center justify-center"
            ></i>
            <h2 class="font-semibold text-gray-800">Appearance</h2>
          </div>

          <div class="grid grid-cols-1 md:grid-cols-2 gap-8 items-center">
            <div class="space-y-1.5 col-span-2">
              <label
                class="block text-sm font-semibold text-slate-700 uppercase tracking-wider"
                >Background*</label
              >
              <select
                v-model="formData.background"
                class="w-full px-4 py-2.5 rounded-xl border border-slate-200 outline-none transition-all focus:border-purple-500"
              >
                <option
                  v-for="(label, val) in backgroundOptions"
                  :key="val"
                  :value="val"
                >
                  {{ label }}
                </option>
              </select>
              <img
                :src="`/images/challenges/${formData.mode}/${formData.background}.png`"
                class="max-h-50 object-contain drop-shadow-md rounded-lg"
              />
            </div>
          </div>
        </section>

        <section
          class="bg-white rounded-3xl p-6 shadow-sm border border-slate-100"
        >
          <div class="flex flex-wrap items-center justify-between gap-4 mb-6">
            <div class="flex items-center gap-3">
              <i
                class="fa-solid fa-list-check text-green-500 w-10 h-10 bg-green-100 rounded-xl flex items-center justify-center"
              ></i>
              <h2 class="font-semibold text-gray-800">Tasks</h2>
            </div>

            <button
              @click="addTask('add')"
              type="button"
              class="flex items-center gap-2 px-4 py-2 bg-emerald-50 text-emerald-700 border border-emerald-100 rounded-xl font-semibold text-sm hover:bg-emerald-100 transition-all shadow-sm"
            >
              ADD <i class="fa-solid fa-plus text-[10px]"></i>
            </button>
          </div>

          <div class="grid grid-cols-1 md:grid-cols-2 gap-2.5">
            <div
              v-for="(curr, index) in formData.tasks"
              :key="index"
              class="relative py-2 px-3 bg-slate-50 rounded-2xl border border-slate-200 group transition-all hover:border-emerald-200 hover:bg-white"
            >
              <button
                v-if="formData.tasks.length > 1"
                @click="addTask('remove', index)"
                class="absolute -top-2 -right-2 w-8 h-8 bg-white border border-rose-100 text-rose-500 rounded-full shadow-sm hover:bg-rose-500 transition-all flex items-center justify-center z-10"
              >
                <i data-v-233e46ef="" class="fa-solid fa-times text-xs"></i>
              </button>

              <div class="grid grid-cols-1 md:grid-cols-3 gap-x-3 gap-y-1">
                <div class="space-y-1">
                  <label
                    class="text-[11px] font-bold text-slate-500 uppercase tracking-wider"
                    >Type*</label
                  >
                  <select
                    v-model="formData.tasks[index].type"
                    class="w-full custom-input px-3 py-2 rounded-lg border border-slate-200 text-sm outline-none focus:border-indigo-500 bg-white"
                  >
                    <option
                      v-for="(label, val) in tasksOptions"
                      :key="val"
                      :value="val"
                    >
                      {{ label }}
                    </option>
                  </select>
                </div>

                <div class="space-y-1">
                  <label
                    class="text-[11px] font-bold text-slate-500 uppercase tracking-wider"
                  >
                    {{ getLabelTask(formData.tasks[index].type) }}
                  </label>
                  <input
                    v-model="formData.tasks[index].value"
                    type="text"
                    class="w-full px-3 py-2 rounded-lg border border-slate-200 text-sm outline-none focus:border-indigo-500 bg-white custom-input"
                    @input="handleNumericInput($event, 'value')"
                  />
                </div>

                <div
                  v-if="formData.tasks[index].type === 'catch_multipliers'"
                  class="space-y-1"
                >
                  <label
                    class="text-[11px] font-bold text-slate-500 uppercase tracking-wider"
                  >
                    {{ getLabelMultiplier(formData.tasks[index].type) }}
                  </label>
                  <input
                    v-model="formData.tasks[index].multiplier_reached"
                    type="text"
                    class="w-full px-3 py-2 rounded-lg border border-slate-200 text-sm outline-none focus:border-indigo-500 bg-white custom-input"
                    @input="handleNumericInput($event, 'multiplier_reached')"
                  />
                </div>

                <div class="space-y-1">
                  <label
                    class="text-[11px] font-bold text-slate-500 uppercase tracking-wider"
                    >Min Bet</label
                  >
                  <input
                    v-model="formData.tasks[index].min_bet"
                    type="text"
                    class="w-full px-3 py-2 rounded-lg border border-slate-200 text-sm outline-none focus:border-indigo-500 bg-white custom-input"
                    placeholder="0"
                    @input="handleNumericInput($event, 'min_bet')"
                  />
                </div>

                <div class="space-y-1">
                  <label
                    class="text-[11px] font-bold text-slate-500 uppercase tracking-wider"
                    >Max Bet</label
                  >
                  <input
                    v-model="formData.tasks[index].max_bet"
                    type="text"
                    class="w-full px-3 py-2 rounded-lg border border-slate-200 text-sm outline-none focus:border-indigo-500 bg-white custom-input"
                    placeholder="0"
                    @input="handleNumericInput($event, 'max_bet')"
                  />
                </div>
              </div>
            </div>
          </div>
        </section>

        <section
          class="bg-white rounded-3xl p-6 shadow-sm border border-slate-100"
        >
          <div class="flex flex-wrap items-center justify-between gap-4 mb-6">
            <div class="flex items-center gap-3">
              <i
                class="fa-solid fa-trophy text-yellow-500 w-10 h-10 bg-yellow-100 rounded-xl flex items-center justify-center"
              ></i>

              <h2 class="font-semibold text-gray-800">Prizes</h2>
            </div>

            <button
              @click="addPrize('add')"
              type="button"
              class="flex items-center gap-2 px-4 py-2 bg-emerald-50 text-emerald-700 border border-emerald-100 rounded-xl font-bold text-sm hover:bg-emerald-100 transition-all shadow-sm"
            >
              ADD PRIZE <i class="fa-solid fa-plus text-[10px]"></i>
            </button>
          </div>

          <div
            v-if="formData.mode === 'race'"
            class="mb-6 max-w-xs space-y-1.5"
          >
            <label
              class="block text-sm font-bold text-slate-700 uppercase tracking-wider"
              >Max Players*</label
            >
            <input
              v-model.number="formData.max_players"
              type="number"
              class="w-full px-4 py-2.5 rounded-xl border border-slate-200 outline-none focus:border-rose-500 focus:ring-4 focus:ring-rose-50 custom-input"
              placeholder="10"
              @input="handleNumericInput($event, 'max_players')"
            />
          </div>

          <div class="grid grid-cols-1 md:grid-cols-2 gap-2.5">
            <div
              v-for="(prize, index) in formData.prizes"
              :key="index"
              class="relative py-2 px-3 bg-slate-50 rounded-2xl border border-slate-200 group transition-all hover:border-emerald-200 hover:bg-white"
            >
              <button
                v-if="formData.prizes.length > 1"
                @click="addPrize('remove', index)"
                type="button"
                class="absolute -top-2 -right-2 w-8 h-8 bg-white border border-rose-100 text-rose-500 rounded-full shadow-md hover:bg-rose-500 transition-all flex items-center justify-center z-10"
              >
                <i class="fa-solid fa-times text-xs"></i>
              </button>

              <div class="mb-2.5">
                <span
                  class="text-[10px] font-semibold text-slate-500 uppercase bg-blue-100 px-3 py-1.5 rounded-lg block w-fit max-w-[90%]"
                >
                  {{ getDescTypePrize(formData.prizes[index].typeprize) }}
                </span>
              </div>

              <div class="grid grid-cols-1 md:grid-cols-3 gap-x-3 gap-y-1">
                <template v-if="formData.mode === 'tournament'">
                  <div class="space-y-1.5">
                    <label
                      class="text-[11px] font-bold text-slate-400 uppercase tracking-wider"
                      >Min Rank*</label
                    >
                    <input
                      v-model="formData.prizes[index].min_rank"
                      type="text"
                      class="w-full px-3 py-2 rounded-lg border border-slate-200 text-sm outline-none focus:border-rose-500 bg-white custom-input"
                      @input="handleNumericInput($event, index, 'min_rank')"
                    />
                  </div>
                  <div class="space-y-1.5">
                    <label
                      class="text-[11px] font-bold text-slate-400 uppercase tracking-wider"
                      >Max Rank*</label
                    >
                    <input
                      v-model="formData.prizes[index].max_rank"
                      type="text"
                      class="w-full px-3 py-2 rounded-lg border border-slate-200 text-sm outline-none focus:border-rose-500 bg-white custom-input"
                      @input="handleNumericInput($event, index, 'max_rank')"
                    />
                  </div>
                </template>

                <div class="space-y-1.5">
                  <label
                    class="text-[11px] font-bold text-slate-400 uppercase tracking-wider"
                    >Type Prize*</label
                  >
                  <select
                    v-model="formData.prizes[index].typeprize"
                    @change="onTypePrizeChange(index)"
                    class="w-full px-3 py-2 rounded-lg border border-slate-200 text-sm outline-none focus:border-rose-500 bg-white cursor-pointer"
                  >
                    <option value="freebets">Freebets</option>
                    <option value="custom">Custom</option>
                  </select>
                </div>

                <div
                  v-if="formData.prizes[index].typeprize === 'custom'"
                  class="space-y-1.5 col-span-full"
                >
                  <label
                    class="text-[11px] font-bold text-slate-400 uppercase tracking-wider"
                    >Link</label
                  >
                  <input
                    v-model="formData.prizes[index].link"
                    type="text"
                    class="w-full px-3 py-2 rounded-lg border border-slate-200 text-sm outline-none focus:border-rose-500 bg-white custom-input"
                    placeholder="https://..."
                  />
                </div>

                <template v-else>
                  <div
                    v-if="formData.prizes[index].typeprize === 'freebets_boost'"
                    class="space-y-1.5"
                  >
                    <label
                      class="text-[11px] font-bold text-slate-400 uppercase tracking-wider"
                      >Boost</label
                    >
                    <input
                      v-model="formData.prizes[index].boost"
                      type="text"
                      class="w-full px-3 py-2 rounded-lg border border-slate-200 text-sm outline-none focus:border-rose-500 bg-white custom-input"
                    />
                  </div>
                  <div class="space-y-1.5">
                    <label
                      class="text-[11px] font-bold text-slate-400 uppercase tracking-wider"
                      >Qty per Player</label
                    >
                    <input
                      v-model.number="formData.prizes[index].quantity"
                      type="number"
                      class="w-full px-3 py-2 rounded-lg border border-slate-200 text-sm outline-none focus:border-rose-500 bg-white custom-input"
                      @input="handleNumericInput($event, index, 'quantity')"
                    />
                  </div>
               
                  <div class="space-y-1.5">
                    <label
                      class="text-[11px] font-bold text-slate-400 uppercase tracking-wider"
                      >Amount</label
                    >
                    <input
                      v-model="formData.prizes[index].amount"
                      type="text"
                      class="w-full px-3 py-2 rounded-lg border border-slate-200 text-sm outline-none focus:border-rose-500 bg-white custom-input"
                      @input="handleNumericInput($event, index, 'amount')"
                    />
                  </div>
                   
              
                </template>
              </div>
            </div>
          </div>
        </section>

        <div
          class="flex items-center justify-end pt-6 border-t border-gray-200 gap-2"
        >
          <button
            @click="goBack"
            class="px-6 py-2.5 h-auto w-fit rounded-xl bg-slate-200 text-slate-700 font-semibold hover:bg-slate-300 transition-all active:scale-95"
          >
            {{ $t("util.back") }}
          </button>
          <button
            @click="handleSubmit"
            :disabled="Object.keys(validationErrors).length > 0"
            class="text-teal-900 bg-teal-100 inline-flex items-center gap-2 rounded-xl px-4 py-2 transition-colors duration-150 active:scale-95 border border-teal-200"
          >
            <span>{{ $route.params.id ? "UPDATE" : "CREATE" }}</span>
            <i class="fa-solid fa-cloud-bolt"></i>
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import Swal from "sweetalert2";
import { format } from "date-fns";
import { mapGetters } from "vuex/dist/vuex.cjs.js";
import FlatPickr from "vue-flatpickr-component";
import "flatpickr/dist/flatpickr.css";
import { postFetch, getFetch, putFetch } from "@/api/axiosClient";
import { parse } from "date-fns";
import Loader from "./Loader.vue";
import store from "@/store/index";
const clickOutside = {
  beforeMount(el, binding) {
    el.clickOutsideEvent = (event) => {
      if (!(el === event.target || el.contains(event.target))) {
        binding.value(event);
      }
    };
    document.addEventListener("click", el.clickOutsideEvent);
  },
  unmounted(el) {
    document.removeEventListener("click", el.clickOutsideEvent);
  },
};
export default {
  name: "ChallengeGenerator",
  components: {
    FlatPickr,
    Loader,
  },
  directives: { "click-outside": clickOutside },
  data() {
    return {
      activeDropdown: null,
      searchQueries: { game: "", currency: "" },
      isLoading: false,
      gameChoose: {},
      validationErrors: {},
      formData: {
        title: "",
        description: "",
        note: "",
        mode: "rain",
        background: "green",
        skin_id: null,
        start_date: null,
        announcement_date: null,
        end_date: null,
        max_players: 1,
        prizes: [
          {
            typeprize: "freebets",
            quantity: 1,
            min_cashout: 1,
            amount: 10,
            type: "all",
            link: "",
            boost: 2,
            min_rank: 1,
            max_rank: 1,
          },
        ],
        tasks: [
          {
            type: "bet_total",
            value: 0,
            min_bet: 0,
            max_bet: 0,
            multiplier_reached: 1,
          },
        ],
        mode: "mission",
        is_enabled: true,
        min_bet: 0,
        max_bet: 0,
        currency: "EUR",
        games: [],
      },
      modeOptions: {
        mission: "Mission",
        race: "Race",
        tournament: "Tournament",
      },
      gameIDOptions: {
        augustusburger: "Augustus Burger",
        candydreams: "Candy Dreams",
        deeptreasure: "Deep Treasure",
        dragonstale: "Dragons'Tale",
        egyptianbook: "Egyptian Book",
        fruityhills: "Fruity Hills",
        goldenpot: "Golden Pot",
        jokearparty: "Joker Party",
        jungleglade: "Jungle Glade",
        revolverduels: "Revolver Duels",
        riosambaspin: "Rio Samba Spin",
        slotbar90: "Slot Bar 90'",
        straighttojail: "Straight To Jail",
        tigerblessing: "Tiger Blessing",
        vampirecastle: "Vampire Castle",
        zeuswrath: "Zeus' Wrath",
      },
      selectedDatabase: "theearcasino",
      currenciesOpt: [],
      backgroundOptions: {
        green: "Green",
        blue: "Blue",
        purple: "Purple",
        orange: "Orange",
        red: "Red",
        pink: "Pink",
        yellow: "Yellow",
      },
      tasksOptions: {
        bet_total: "Bet a Total",
        play_rounds: "Play Rounds",
        win_money: "Win Money",
        win_rounds: "Win Rounds",
      },
    };
  },
  computed: {
    ...mapGetters([
      "isMenuOpen",
      "allowedSkins",
      "allowedGamesFreebet",
      "crashGameOptions",
      "crashMultiplierGameOptions",
    ]),
    filteredGames() {
      const q = this.searchQueries.game.toLowerCase();
      if (!q) return this.gameChoose;
      return Object.fromEntries(
        Object.entries(this.gameChoose).filter(([_, label]) =>
          label.toLowerCase().includes(q),
        ),
      );
    },
  },
  methods: {
    async fetchGames() {
      try {
        const res = await getFetch(`/games`);
        const games = Array.isArray(res) ? res : res?.data || [];
        this.gameChoose = Object.fromEntries(
          games.filter((g) => g.is_enabled).map((g) => [g.game_id, g.name]),
        );
 
      } catch (e) {
        console.error("Fetch games error", e);
        this.gameChoose = {};
      }
    },
    toggleDropdown(key) {
      if (this.activeDropdown === key) {
        this.activeDropdown = null;
      } else {
        this.activeDropdown = key;
        if (key === "game") {
          this.searchQueries.game = "";
          this.$nextTick(() => this.$refs.gameSearchInput?.focus());
        } else {
          this.searchQueries.currency = "";
        }
      }
    },
    closeDropdown(key) {
      if (this.activeDropdown === key) this.activeDropdown = null;
    },
    removeGame(id) {
      this.formData.games = this.formData.games.filter(gameId => gameId !== id);
    },
     selectGame(id) {
      if (!Array.isArray(this.formData.games)) {
        this.formData.games = [];
      }
      if (!this.formData.games.includes(id)) {
        this.formData.games.push(id);
      } else {
        this.removeGame(id);
      }
    },
    onTypePrizeChange(index) {
      const prize = this.formData.prizes[index];
      if (prize.typeprize !== "freebets") prize.type = "cashout_1_cashout_2";
      else prize.type = "all";
    },
    addTask(type, index = null) {
      if (type === "add") {
        this.formData.tasks.push({
          type: "bet_total",
          value: 0,
          min_bet: 0,
          max_bet: 0,
          multiplier_reached: 1,
        });
      } else if (type === "remove" && this.formData.tasks.length > 1) {
        this.formData.tasks.splice(index, 1);
      }
    },
    addPrize(type, index = null) {
      if (type === "add") {
        this.formData.prizes.push({
          typeprize: "freebets",
          quantity: 1,
          min_cashout: 1,
          amount: 10,
          type: "all",
          link: "",
          boost: 2,
          min_rank: 1,
          max_rank: 1,
        });
      } else if (type === "remove" && index !== null) {
        this.formData.prizes.splice(index, 1);
      }
    },
    getLabelTask(taskType) {
      const labels = {
        bet_total: "Total Bets*",
        collect_multipliers: "Total Multipliers*",
        play_rounds: "Total Rounds*",
        win_money: "Total Win Amount*",
        win_rounds: "Total Win Rounds*",
        catch_multipliers: "Number of times*",
      };
      return labels[taskType] || "Value*";
    },
    getLabelMultiplier(taskType) {
      const labels = {
        play_rounds: "Multiplier is Reached",
        catch_multipliers: "Min Multiplier",
      };
      return labels[taskType] || "Value*";
    },
    getDescMultiplier(taskType) {
      const labels = {
        play_rounds:
          "Optional: To complete this task, player should make a bet in a round where entered multiplier was reached. The task is completed both if he wins or loses the round. Example: You entered 10x here. If player has placed a bet in a round where multiplier goes to 10x or more, this task is completed.",
        catch_multipliers: "minimum multiplier to complete the task.",
      };
      return labels[taskType] || "Value*";
    },
    getDescTask(taskType) {
      let descriptions = {};
      if (this.formData.mode === "tournament") {
        descriptions = {
          bet_total: "total bets to get 1 point",
          collect_multipliers: "Total multipliers to get 1 point",
          play_rounds: "Total rounds to play to get 1 point",
          win_money: "Total amount of money to win to get 1 point",
          win_rounds: "Total rounds to win to get 1 point",
          catch_multipliers:
            "Number of times you need to cashout to get 1 point.",
        };
      } else {
        descriptions = {
          bet_total: "Total bets to place to complete the task",
          collect_multipliers:
            "Total multipliers to collect to complete the task",
          play_rounds: "Total rounds to play to complete the task",
          win_money: "Total amount of money to win to complete the task",
          win_rounds: "Total rounds to win to complete the task",
          catch_multipliers:
            "Number of times you need to cashout to complete the task.",
        };
      }
      return descriptions[taskType] || "Value required";
    },
    getDescTypePrize(type) {
      const descriptions = {
        freebets: "Classic freebets prize",
        freebets_boost:
          "Free bets with a boosted multiplier: the cashout the player wins will be multiplied by the boost inserted.",
        freebets_lock:
          "Lock in your winnings: Don't worry about what happens, just lock in the multiplier and win that amount. After locking to win even more!",
        freebets_final:
          "Wathever the final multiplier is when the round ends, that's what you win!",
        custom: "Link to custom prize",
      };
      return descriptions[type] || "";
    },
    parseDate(input) {
      let [day, month, year] = input.split("/");
      return new Date(`${year}-${month}-${day}`);
    },
    minDateStart() {
      let today = new Date();
      let startDate = this.formData.start_date
        ? this.parseDate(this.formData.start_date)
        : null;
      return startDate && startDate > today
        ? new Date()
        : this.formData.start_date;
    },
    minDateNow() {
      const today = new Date();
      today.setHours(0, 0, 0, 0); // sets time to 00:00:00.000
      return today;
    },
    setRequiredFieldHighlight(val) {
      const requiredFields = [
        "title",
        "description",
        "skin_id",
        "background",
        "start_date",
        "announcement_date",
        "end_date",
        "min_bet",
        "max_bet",
        "tasks",
        "mode",
        "is_enabled",
        "currency",
      ];

      let hasMissingField = false;

      requiredFields.forEach((field) => {
        const value = this.formData[field];
        const isEmpty =
          value === "" ||
          value === " " ||
          value === null ||
          value === undefined;

        if (isEmpty) {
          this.validationErrors[field] = val;
          hasMissingField = true;
        }
      });

      if (hasMissingField && val) {
        Swal.fire({
          title: this.$t("rain.error_title") ?? "Error!",
          text: "An error occurred while creating the Challenge. Check if you have entered all the fields.",
          icon: "error",
        });
      }

      setTimeout(() => {
        this.validationErrors = {};
      }, 4500);
    },
    formatCurrency(amount, currency) {
      if (amount === 0) return `0.00 ${currency}`;
      return `${parseFloat(amount).toFixed(2)} ${currency}`;
    },
    checkValue(key) {
      let value = this.formData[key];

      if (value === null || value === undefined || value === "") {
        this.formData[key] = 0;
      } else if (typeof value !== "number" || isNaN(value)) {
        this.formData[key] = 0;
      } else if (value < 0) {
        this.formData[key] = 0;
      }
    },

    handleNumericInput(event, key) {
      let input = event.target.value;

      if (input === "") {
        input = "0";
      }

      input = input.replace(/,/g, ".");
      input = input.replace(/[^0-9.]/g, "");

      const parts = input.split(".");
      if (parts.length > 2) {
        input = parts[0] + "." + parts.slice(1).join("");
      }

      if (input.includes(".")) {
        const [intPart, decimalPart] = input.split(".");
        input = intPart + "." + decimalPart.slice(0, 2);
      }
      this.formData[key] = input;
    },
    goBack() {
      const lang = this.$route.params.lang || "en";
      const skin = this.$route.params.skinFilt || this.$store.getters.skinStorage;
      this.$router.push({
        name: "Challenges",
        params: { lang, ...(skin ? { skinFilt: skin.toString() } : {}) },
      });
    },

    async handleSubmit() {
      this.validationErrors = {};

      this.setRequiredFieldHighlight(true);
 

      const convertDateToISOString = (dateString) => {
        if (!dateString) return null;

        const normalized = dateString.replace(/-/g, "/");
        const [datePart, timePart = "00:00"] = normalized.split(" ");
        const [day, month, year] = datePart.split("/");
        const [hour, minute] = timePart.split(":");
        const date = new Date(Date.UTC(year, month - 1, day, hour, minute));
        return isNaN(date) ? null : date.toISOString();
      };

      const dataToSend = { ...this.formData };

      const dateFields = [
        { key: "start_date" },
        { key: "end_date" },
        { key: "announcement_date" },
      ];

      for (const { key, type } of dateFields) {
        if (dataToSend[key]) {
          const iso = convertDateToISOString(dataToSend[key]);
          // console.log(iso);
          if (iso) {
            dataToSend[key] = iso;
          } else {
            Swal.fire({
              title: this.$t("rain.error_title") ?? "Error!",
              text: `${
                this.$t("rain.invalid_date_error") ?? "Invalid date format in"
              } ${key.replace("_", " ")}.`,
              icon: "error",
            });
            return;
          }
        }
      }

      const numericFields = ["amount", "min_bet", "max_bet"];

      const numericFieldsInt = ["min_wager"];
      dataToSend.prizes.forEach((prize, key) => {
        dataToSend.prizes[key].quantity = parseInt(prize.quantity);
        dataToSend.prizes[key].min_cashout = parseFloat(prize.min_cashout);
        dataToSend.prizes[key].amount = parseFloat(prize.amount);
        dataToSend.prizes[key].boost = parseFloat(prize.boost);
        dataToSend.prizes[key].min_rank = parseInt(prize.min_rank);
        dataToSend.prizes[key].max_rank = parseInt(prize.max_rank);
      });

      dataToSend.tasks.forEach((task, key) => {
        dataToSend.tasks[key].value = parseFloat(task.value);
        dataToSend.tasks[key].min_bet = parseFloat(task.min_bet);
        dataToSend.tasks[key].max_bet = parseFloat(task.max_bet);
        dataToSend.tasks[key].multiplier_reached = parseFloat(
          task.multiplier_reached,
        );
      });

      numericFields.forEach((field) => {
        if (dataToSend[field] !== undefined) {
          dataToSend[field] = parseFloat(dataToSend[field]).toFixed(2);
        }
      });

      numericFieldsInt.forEach((field) => {
        if (dataToSend[field] !== undefined) {
          dataToSend[field] = parseInt(dataToSend[field]);
        }
      });

      if (this.$route.params.id) {
        try {
          await putFetch(`/campaigns/${this.$route.params.id}`, dataToSend);

          Swal.fire("Success!", "The challenge has been updated.").then(() => {
            window.location.reload();
          });
        } catch (error) {
          console.error("Error updating challenge detail:", error);
          Swal.fire({
            title: "Error",
            text: "Failed to update the challenge detail.",
            icon: "error",
          });
        }
      } else {
        try {
          const response = await postFetch(`campaigns`, dataToSend);
          const lang = this.$route.params.lang || "en";
          const skin = this.$route.params.skinFilt || this.$store.getters.skinStorage;
          this.$router.push({
            name: "Challenges",
            params: { lang, ...(skin ? { skinFilt: skin.toString() } : {}) },
          });
        } catch (error) {
          console.log(error);
        }
      }
    },

    setInitialDate(data = null) {
      if (data == null) {
        //Start
        let startDate = new Date();
        startDate.setDate(startDate.getDate());
        let day = String(startDate.getDate()).padStart(2, "0");
        let month = String(startDate.getMonth() + 1).padStart(2, "0"); // I mesi partono da 0
        let year = startDate.getFullYear();
        startDate = `${day}/${month}/${year} 00:00`;
        //end
        let endDate = new Date();
        endDate.setDate(endDate.getDate() + 8);
        day = String(endDate.getDate()).padStart(2, "0");
        month = String(endDate.getMonth() + 1).padStart(2, "0"); // I mesi partono da 0
        year = endDate.getFullYear();
        endDate = `${day}/${month}/${year} 00:00`;

        //announcement date
        let announcementDate = new Date();
        announcementDate.setDate(announcementDate.getDate());
        day = String(announcementDate.getDate()).padStart(2, "0");
        month = String(announcementDate.getMonth() + 1).padStart(2, "0"); // I mesi partono da 0
        year = announcementDate.getFullYear();
        announcementDate = `${day}/${month}/${year} 00:00`;

        this.formData = {
          ...this.formData,
          start_date: startDate,
          announcement_date: announcementDate,
          end_date: endDate,
        };
      } else {
        let rawStart = data.start_date;
        let rawEnd = data.end_date;
        let rawAnnounc = data.announcement_date;

        //2025-10-24T00:00:00.000Z
        const parsedStart = parse(rawStart, "yyyy-MM-dd HH:mm:ss", new Date());
        const parsedEnd = parse(rawEnd, "yyyy-MM-dd HH:mm:ss", new Date());
        const parsedAnnounc = parse(
          rawAnnounc,
          "yyyy-MM-dd HH:mm:ss",
          new Date(),
        );
        // console.log(parsedStart);
        // console.log(parsedEnd);
        // console.log(parsedAnnounc);
        const formattedStart = format(parsedStart, "dd/MM/yyyy HH:mm");
        const formattedEnd = format(parsedEnd, "dd/MM/yyyy HH:mm");
        const formattedAnnounc = format(parsedAnnounc, "dd/MM/yyyy HH:mm");

        this.formData = {
          ...this.formData,
          start_date: formattedStart,
          end_date: formattedEnd,
          announcement_date: formattedAnnounc,
        };
      }
    },
    async checkSkin() {
      const params = new URLSearchParams(window.location.search);
      const skinFromQuery = params.get("skin_id");
      const paramSkin = this.$route.params.skinFilt;
      let skin = skinFromQuery ?? (paramSkin && paramSkin !== "all" ? paramSkin : this.$store.getters.skinStorage);

      if (skin) {
        this.formData.skin_id = parseInt(skin) || skin;
      }
    },

    async fetchCurrencySuggestions() {
      try {
        let query = "";
        const response = await getFetch(`currencies`, { query });
        this.currenciesOpt = response;
      } catch (err) {
        console.error("Errore durante la ricerca delle valute:", err.message);
        this.currenciesOpt = [];
      }
    },
    async fetchRainDetails() {
      const campaignId_params =
        this.$route.params.id ?? this.$route.query.campaign_id;
      if (!campaignId_params) {
        this.setInitialDate();
        return;
      } else {
        this.isLoading = true;
        try {
          const data = await getFetch(`campaigns/${campaignId_params}`);
          if (!data || Object.keys(data).length === 0) return;

          this.formData = {
            ...this.formData,
            skin_id: parseInt(data.skin_id),
            background: data.background,
            games: data.games,
            min_bet: data.min_bet ?? 0,
            max_bet: data.max_bet ?? 0,
            start_date: null,
            announcement_date: null,
            end_date: null,
            description: data.description,
            max_players: data.max_players ?? 0,
            title: data.title,
            note: data.note,
            type: data.type,
            tasks: data.tasks,
            prizes: data.prizes,
            currency: data.currency,
            amount: Number(data.amount),
            min_wager: Number(data?.min_wager ?? 0),
            is_enabled: !!data.is_enabled,
            mode: data.mode,
          };

          this.setInitialDate(data);
        } catch (error) {
          console.error(
            "Errore durante il recupero dei dettagli della campaigns:",
            error.message,
          );
        } finally {
          this.isLoading = false;
        }
      }
    },
  },
  async mounted() {
    import("@/assets/styles/components/swal2ModalDialog.scss");
    await this.checkSkin();

    this.fetchRainDetails();

    await this.fetchCurrencySuggestions();
    this.fetchGames();

    // this.gameChoose = this.allowedGamesFreebet;
  },
  beforeUnmount() {
    const styles = document.querySelectorAll(
      'link[href*="swal2ModalDialog.scss"], style[data-vue-ssr-id*="swal2ModalDialog.scss"]',
    );
    styles.forEach((style) => style.remove());
  },
};
</script>

<style scoped>
.custom-input {
  @apply w-full px-3 py-2 rounded-xl !border !border-slate-200 focus:!ring-4 focus:!ring-emerald-500/10 focus:!border-emerald-500 transition-all outline-none !text-black;
}

ul::-webkit-scrollbar {
  width: 4px;
}
ul::-webkit-scrollbar-track {
  background: transparent;
}
ul::-webkit-scrollbar-thumb {
  background: #e2e8f0;
  border-radius: 10px;
}

label {
  text-transform: uppercase;
  letter-spacing: 0.05em;
}

textarea,
select,
input {
  color: black;
}
</style>
